import { BgColorsOutlined, CheckOutlined, MoonOutlined, SunOutlined } from '@ant-design/icons'
import { Button, Popover, Segmented, Tooltip } from 'antd'
import { useState } from 'react'

import { useAppDispatch, useAppSelector } from '../../app/store'
import {
  setDashboardStyle,
  setLanguage,
  setThemeMode,
  setThemeStyle,
  toggleThemeMode,
  type DashboardStyle,
} from '../../app/uiSlice'
import i18n from '../../i18n'
import type { ThemeStyle } from '../../theme'

import './AppearanceMenu.css'
import ThemeToggle from './ThemeToggle'

const STYLES: { key: ThemeStyle; title: string; hint: string; badge?: string }[] = [
  { key: 'classic', title: 'الكلاسيكي', hint: 'كحلي وذهبي — هوية أفق الأصلية' },
  { key: 'studio', title: 'ستوديو', hint: 'أزرق حيّ وبطاقات عائمة', badge: 'جديد' },
]

/** معاينة مصغّرة لكل ثيم — قائمة جانبية وترويسة وبطاقات ورسم، بألوانه الحقيقية */
function Preview({ style }: { style: ThemeStyle }) {
  return (
    <div className={`apm-preview apm-${style}`} aria-hidden>
      <div className="apm-side">
        <i className="apm-logo" />
        <i className="apm-nav on" />
        <i className="apm-nav" />
        <i className="apm-nav" />
        <i className="apm-nav" />
        {style === 'studio' && <i className="apm-promo" />}
      </div>
      <div className="apm-main">
        <div className="apm-top">
          <i className="apm-search" />
          <i className="apm-dot" />
          <i className="apm-dot" />
        </div>
        <div className="apm-kpis">
          <i />
          <i />
          <i />
        </div>
        <div className="apm-chart">
          <svg viewBox="0 0 100 34" preserveAspectRatio="none">
            <path
              className="apm-area"
              d="M0 30 L14 22 L28 25 L42 13 L56 18 L70 8 L84 14 L100 5 L100 34 L0 34 Z"
            />
            <path className="apm-line" d="M0 30 L14 22 L28 25 L42 13 L56 18 L70 8 L84 14 L100 5" />
          </svg>
        </div>
      </div>
    </div>
  )
}

/**
 * «المظهر»: اختيار ثيم النظام (الكلاسيكي أو «ستوديو») والوضع الفاتح/الداكن — يُحفظ على الجهاز.
 * في الكلاسيكي يبقى زرّ الوضع المعهود بجانبه، وفي «ستوديو» يصير زرّاً دائرياً كالمرجع.
 */
export default function AppearanceMenu() {
  const dispatch = useAppDispatch()
  const { themeMode, themeStyle, language, dashboardStyle } = useAppSelector((s) => s.ui)
  const [open, setOpen] = useState(false)

  const panel = (
    <div className="apm-panel">
      <div className="apm-head">
        <strong>المظهر</strong>
        <span>اختر شكل النظام — يُحفظ على هذا الجهاز</span>
      </div>
      <div className="apm-grid" role="radiogroup" aria-label="ثيم النظام">
        {STYLES.map((s) => {
          const on = themeStyle === s.key
          return (
            <button
              key={s.key}
              type="button"
              role="radio"
              aria-checked={on}
              className={`apm-card${on ? ' on' : ''}`}
              onClick={() => dispatch(setThemeStyle(s.key))}
            >
              <Preview style={s.key} />
              <span className="apm-title">
                {s.title}
                {s.badge && <em>{s.badge}</em>}
              </span>
              <span className="apm-hint">{s.hint}</span>
              {on && (
                <span className="apm-check">
                  <CheckOutlined />
                </span>
              )}
            </button>
          )
        })}
      </div>
      <div className="apm-mode">
        <span>لوحة القيادة</span>
        <Segmented
          value={dashboardStyle}
          onChange={(v) => dispatch(setDashboardStyle(v as DashboardStyle))}
          options={[
            { value: 'theme', label: 'حسب الثيم' },
            { value: 'prestige', label: 'الفاخر' },
          ]}
        />
      </div>
      <div className="apm-mode">
        <span>الوضع</span>
        <Segmented
          value={themeMode}
          onChange={(v) => dispatch(setThemeMode(v as 'light' | 'dark'))}
          options={[
            { value: 'light', label: 'فاتح', icon: <SunOutlined /> },
            { value: 'dark', label: 'داكن', icon: <MoonOutlined /> },
          ]}
        />
      </div>
      <div className="apm-mode">
        <span>اللغة</span>
        <Segmented
          value={language}
          onChange={(v) => {
            const lng = v as 'ar' | 'en'
            i18n.changeLanguage(lng)
            dispatch(setLanguage(lng))
          }}
          options={[
            { value: 'ar', label: 'العربية' },
            { value: 'en', label: 'English' },
          ]}
        />
      </div>
    </div>
  )

  const trigger =
    themeStyle === 'studio' ? (
      // «ستوديو»: رابط نصي بأيقونة كـ «الإعداد» في المرجع
      <button type="button" className="st-pill-btn ghost" aria-label="المظهر">
        <BgColorsOutlined />
        <span className="st-pill-label">المظهر</span>
      </button>
    ) : (
      <Tooltip title={open ? undefined : 'المظهر'}>
        <Button
          type="text"
          shape="circle"
          size="large"
          aria-label="المظهر"
          icon={<BgColorsOutlined />}
        />
      </Tooltip>
    )

  return (
    <>
      {/* الكلاسيكي: زرّ الوضع المعهود بجانبه — «ستوديو» له مفتاحه الكبسولي في الترويسة */}
      {themeStyle !== 'studio' && <ThemeToggle />}
      <Popover
        open={open}
        onOpenChange={setOpen}
        trigger="click"
        placement="bottomLeft"
        arrow={false}
        content={panel}
        styles={{ container: { padding: 0 } }}
      >
        {trigger}
      </Popover>
    </>
  )
}

/**
 * مفتاح الوضع بثيم «ستوديو» — كبسولة بيضاء بمقبض يحمل الشمس أو القمر (كالمرجع).
 */
export function ModePill() {
  const dispatch = useAppDispatch()
  const isDark = useAppSelector((s) => s.ui.themeMode === 'dark')
  return (
    <Tooltip title={isDark ? 'الوضع الفاتح' : 'الوضع الداكن'}>
      <button
        type="button"
        role="switch"
        aria-checked={isDark}
        aria-label={isDark ? 'تفعيل الوضع الفاتح' : 'تفعيل الوضع الداكن'}
        className={`st-mode${isDark ? ' dark' : ''}`}
        onClick={() => dispatch(toggleThemeMode())}
      >
        <span className="st-mode-knob">{isDark ? <MoonOutlined /> : <SunOutlined />}</span>
      </button>
    </Tooltip>
  )
}
